import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  ShoppingBag, Calculator, Tag, Package, Loader2,
  Smartphone, Headphones, Gamepad2, ShoppingCart, Watch, 
  Battery, Wrench, Shield
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { MembershipPlan } from './types';

interface DiscountPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    product_name: string;
    category: string;
    original_price: number;
    discount_percentage: number;
    final_price: number;
    savings: number;
    quantity: number;
    notes: string;
  }) => Promise<void>;
  plan: MembershipPlan;
  memberName: string;
  isSubmitting?: boolean;
}

const discountsByPlan: Record<MembershipPlan, Record<string, number>> = {
  bronce: {
    'Tecnología': 5,
    'Audio': 5,
    'Gaming': 5,
    'Estuches': 5,
    'Relojes': 5,
    'Cargadores': 5,
    'Reparaciones': 10,
    'Vidrios': 5,
  },
  plata: {
    'Tecnología': 10,
    'Audio': 10,
    'Gaming': 10,
    'Estuches': 10,
    'Relojes': 10,
    'Cargadores': 10,
    'Reparaciones': 20,
    'Vidrios': 10,
  },
  oro: {
    'Tecnología': 15,
    'Audio': 20,
    'Gaming': 20,
    'Estuches': 20,
    'Relojes': 10,
    'Cargadores': 20,
    'Reparaciones': 30,
    'Vidrios': 15,
  },
};

const categoryIcons: Record<string, React.ElementType> = {
  'Tecnología': Smartphone,
  'Audio': Headphones,
  'Gaming': Gamepad2,
  'Estuches': ShoppingCart,
  'Relojes': Watch,
  'Cargadores': Battery,
  'Reparaciones': Wrench,
  'Vidrios': Shield,
};

const planColors: Record<MembershipPlan, { bg: string; border: string; text: string; selected: string }> = {
  bronce: {
    bg: 'from-amber-500/10 to-amber-600/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    selected: 'bg-amber-500/20 border-amber-400',
  },
  plata: {
    bg: 'from-slate-400/10 to-slate-500/10',
    border: 'border-slate-400/30',
    text: 'text-slate-300',
    selected: 'bg-slate-400/20 border-slate-300',
  },
  oro: {
    bg: 'from-yellow-500/10 to-amber-500/10',
    border: 'border-yellow-500/30',
    text: 'text-yellow-400',
    selected: 'bg-yellow-500/20 border-yellow-400',
  },
};

const categories = [
  'Tecnología',
  'Audio',
  'Gaming',
  'Estuches',
  'Relojes',
  'Cargadores',
  'Reparaciones',
  'Vidrios',
];

export const DiscountPurchaseModal: React.FC<DiscountPurchaseModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  plan,
  memberName,
  isSubmitting = false,
}) => {
  const [productName, setProductName] = useState('');
  const [category, setCategory] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');

  const colors = planColors[plan];

  const discountPercentage = useMemo(() => {
    if (!category) return 0;
    return discountsByPlan[plan][category] || 0;
  }, [category, plan]);

  const calculations = useMemo(() => {
    const price = parseInt(originalPrice) || 0;
    const total = price * quantity;
    const discountAmount = Math.round(total * (discountPercentage / 100));
    const finalPrice = total - discountAmount;
    return { total, discountAmount, finalPrice };
  }, [originalPrice, quantity, discountPercentage]);

  const handleSubmit = async () => {
    if (!productName.trim() || !category || !originalPrice) return;

    await onConfirm({
      product_name: productName.trim(),
      category,
      original_price: calculations.total,
      discount_percentage: discountPercentage,
      final_price: calculations.finalPrice,
      savings: calculations.discountAmount,
      quantity,
      notes: notes.trim(),
    });

    // Reset form
    setProductName('');
    setCategory('');
    setOriginalPrice('');
    setQuantity(1);
    setNotes('');
  };

  const isValid = productName.trim() && category && parseInt(originalPrice) > 0;

  // Find the best discount for this plan
  const bestDiscount = Math.max(...Object.values(discountsByPlan[plan]));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShoppingBag className="w-5 h-5 text-purple-400" />
            Registrar Compra con Descuento
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Member info */}
          <div className={`bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-xl p-3`}>
            <p className={`${colors.text} text-sm`}>
              Registrando compra para <span className="font-semibold text-white">{memberName}</span>
            </p>
            <p className={`${colors.text} opacity-70 text-xs`}>
              Plan {plan.toUpperCase()} - Selecciona una categoría
            </p>
          </div>

          {/* Category Cards Grid */}
          <div className="space-y-2">
            <Label className="text-white/70">Categoría de producto</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {categories.map((cat) => {
                const Icon = categoryIcons[cat];
                const discount = discountsByPlan[plan][cat];
                const isSelected = category === cat;
                const isBest = discount === bestDiscount;
                
                return (
                  <motion.button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-3 rounded-xl border-2 transition-all ${
                      isSelected 
                        ? `${colors.selected} border-2`
                        : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                    }`}
                  >
                    {isBest && (
                      <span className="absolute -top-1.5 -right-1.5 bg-green-500 text-[10px] text-white font-bold px-1.5 py-0.5 rounded-full">
                        MEJOR
                      </span>
                    )}
                    <div className={`w-8 h-8 rounded-lg mx-auto mb-1.5 flex items-center justify-center ${
                      isSelected ? colors.bg : 'bg-white/10'
                    }`}>
                      <Icon className={`w-4 h-4 ${isSelected ? colors.text : 'text-white/60'}`} />
                    </div>
                    <p className={`text-xs font-medium truncate ${isSelected ? 'text-white' : 'text-white/70'}`}>
                      {cat}
                    </p>
                    <p className={`text-sm font-bold ${isSelected ? colors.text : 'text-blue-400'}`}>
                      -{discount}%
                    </p>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Product name */}
          <div className="space-y-2">
            <Label className="text-white/70">Nombre del producto</Label>
            <div className="relative">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Ej: Audífonos Bluetooth Sony"
                className="bg-white/5 border-white/10 text-white pl-10"
              />
            </div>
          </div>

          {/* Price and quantity */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-white/70">Precio unitario (₡)</Label>
              <Input
                type="number"
                value={originalPrice}
                onChange={(e) => setOriginalPrice(e.target.value)}
                placeholder="20000"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Cantidad</Label>
              <Input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>

          {/* Calculation breakdown */}
          {category && parseInt(originalPrice) > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-4 space-y-2"
            >
              <div className="flex items-center gap-2 text-green-400 font-medium mb-3">
                <Calculator className="w-4 h-4" />
                <span>Resumen de la compra</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Subtotal ({quantity}x ₡{parseInt(originalPrice).toLocaleString()})</span>
                <span className="text-white">₡{calculations.total.toLocaleString()}</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-white/60 flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  Descuento {category} ({discountPercentage}%)
                </span>
                <span className="text-green-400">-₡{calculations.discountAmount.toLocaleString()}</span>
              </div>
              
              <div className="border-t border-white/10 pt-2 mt-2">
                <div className="flex justify-between font-bold">
                  <span className="text-white">Total a pagar</span>
                  <span className="text-green-400 text-lg">₡{calculations.finalPrice.toLocaleString()}</span>
                </div>
                <p className="text-green-400/70 text-xs text-right mt-1">
                  ¡Ahorro de ₡{calculations.discountAmount.toLocaleString()}!
                </p>
              </div>
            </motion.div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-white/70">Notas (opcional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej: Color negro, modelo 2024"
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} className="text-white/60 w-full sm:w-auto">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 w-full sm:w-auto"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ShoppingBag className="w-4 h-4 mr-2" />
            )}
            Registrar Compra
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DiscountPurchaseModal;
